import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "../i18n";
import PageShell from "../components/PageShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import CampaignForm from "../components/campaign/CampaignForm";
import CampaignTable from "../components/campaign/CampaignTable";
import CampaignLog from "../components/campaign/CampaignLog";
import CampaignRunProgress from "../components/campaign/CampaignRunProgress";
import CopySqlButton from "../components/campaign/CopySqlButton";
import SegmentManager from "../components/campaign/SegmentManager";
import {
  useCampaigns,
  useTriggerTypes,
  useCampaignLog,
  useCampaignStats,
  useCampaignRunState,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
  useRunCampaign,
  useStopCampaign,
  useTestCampaign,
  useSegmentDefinitions,
} from "../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

export default function CampaignDashboard() {
  const { t } = useTranslation();

  // Data via tanstack
  const {
    data: campaigns = [],
    isError: campaignsFailed,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns();
  const { data: triggerTypes = [] } = useTriggerTypes();
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();
  const runMutation = useRunCampaign();
  const stopMutation = useStopCampaign();
  const testMutation = useTestCampaign();

  // UI state
  const [selectedId, setSelectedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [logOffset, setLogOffset] = useState(0);

  // Create form state
  const [createMode, setCreateMode] = useState(false);
  const [newTriggerType, setNewTriggerType] = useState("");
  const [newName, setNewName] = useState("");
  const [newConfig, setNewConfig] = useState({});

  // Edit form state
  const [editConfig, setEditConfig] = useState({});
  const [editEnabled, setEditEnabled] = useState(false);
  const [editName, setEditName] = useState("");
  // Tracks whether the edit form holds unsaved changes, so a background
  // refetch of the campaign list cannot silently overwrite them.
  const [editDirty, setEditDirty] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Test campaign dialog
  const [testTarget, setTestTarget] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const fieldsFor = (triggerType) =>
    triggerTypes.find((tt) => tt.triggerType === triggerType)?.configFields ||
    [];

  const placeholdersFor = (triggerType) =>
    triggerTypes.find((tt) => tt.triggerType === triggerType)?.placeholders ||
    [];

  const selectedCampaign = campaigns.find((c) => c.id === selectedId);

  /**
   * Load the selected campaign into the edit form.
   *
   * Skipped while the form is dirty: `campaigns` is invalidated by every
   * mutation, so toggling a switch anywhere on the page used to replace the
   * text in the open edit form with the server's copy and throw away
   * whatever the operator had typed.
   */
  useEffect(() => {
    if (!selectedId) {
      setEditConfig({});
      setEditName("");
      setEditEnabled(false);
      setEditDirty(false);
      return;
    }
    if (editDirty) return;
    const campaign = campaigns.find((c) => c.id === selectedId);
    if (campaign) {
      setEditConfig(campaign.config || {});
      setEditEnabled(campaign.enabled);
      setEditName(campaign.name || "");
    }
  }, [selectedId, campaigns, editDirty]);

  // A finished run changes the numbers the stats/log panels show.
  const { data: logData, isError: logFailed } = useCampaignLog(
    selectedId,
    logOffset,
  );
  const { data: statsData } = useCampaignStats(selectedId);
  const { data: runState } = useCampaignRunState(selectedId);
  // Custom segment names for the `Имя сегмента` picker. Falls back to the
  // global definitions while creating a campaign, which has no id yet.
  const { data: segmentData } = useSegmentDefinitions(selectedId ?? null);
  const customSegmentNames = (segmentData?.items ?? []).map((s) => s.name);

  // Selecting another campaign starts its log from the newest page.
  useEffect(() => {
    setLogOffset(0);
  }, [selectedId]);

  const markEdited = useCallback(() => setEditDirty(true), []);

  // Handlers
  const handleCreate = async () => {
    if (!newTriggerType) {
      toast.error(t("campaign.selectTrigger"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        trigger_type: newTriggerType,
        config: newConfig,
        name: newName,
      });
      toast.success(t("campaign.toastCreated"));
      setCreateMode(false);
      setNewTriggerType("");
      setNewName("");
      setNewConfig({});
    } catch (err) {
      toast.error(describeError(err, t("campaign.toastCreateFailed")));
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedId,
        data: { config: editConfig, enabled: editEnabled, name: editName },
      });
      setEditDirty(false);
      toast.success(t("campaign.toastUpdated"));
    } catch (err) {
      toast.error(describeError(err, t("campaign.toastUpdateFailed")));
    }
  };

  const withBusy = async (id, action) => {
    setBusyId(id);
    try {
      await action();
    } finally {
      setBusyId((current) => (current === id ? null : current));
    }
  };

  const handleToggle = (id, enabled) =>
    withBusy(id, async () => {
      const campaign = campaigns.find((c) => c.id === id);
      if (!campaign) return;
      try {
        await updateMutation.mutateAsync({
          id,
          data: { config: campaign.config, enabled, name: campaign.name },
        });
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastToggleFailed")));
      }
    });

  const handleRun = (id) =>
    withBusy(id, async () => {
      try {
        await runMutation.mutateAsync(id);
        toast.success(t("campaign.toastRunStarted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastRunFailed")));
      }
    });

  const handleStop = (id) =>
    withBusy(id, async () => {
      try {
        await stopMutation.mutateAsync(id);
        toast.success(t("campaign.toastStopRequested"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastStopFailed")));
      }
    });

  const handleTest = (id) => {
    setTestTarget(id);
    setTestPhone("");
  };

  const handleTestSend = async () => {
    if (!testTarget || !testPhone.trim()) return;
    try {
      const result = await testMutation.mutateAsync({
        id: testTarget,
        phone: testPhone.trim(),
      });
      if (result.delivered > 0) {
        toast.success(
          t("campaign.toastTestResult", { name: result.customerName }),
        );
      } else {
        // A "successful" test that reached no device is the case that used to
        // report success while nothing was delivered.
        toast.error(
          t("campaign.toastTestNotDelivered", { name: result.customerName }),
        );
      }
      setTestTarget(null);
      setTestPhone("");
    } catch (err) {
      toast.error(describeError(err, t("campaign.toastTestFailed")));
    }
  };

  const handleDelete = () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    return withBusy(id, async () => {
      try {
        await deleteMutation.mutateAsync(id);
        if (selectedId === id) setSelectedId(null);
        toast.success(t("campaign.toastDeleted"));
      } catch (err) {
        toast.error(describeError(err, t("campaign.toastDeleteFailed")));
      }
    });
  };

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const deleteTargetName =
    deleteTarget != null
      ? campaigns.find((c) => c.id === deleteTarget)?.name || `#${deleteTarget}`
      : "";

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <PageShell title={t("page.marketing")}>
      <div className="flex flex-col gap-4 h-full">
        {/* ── Campaign List ── */}
        <div className="w-full shrink-0">
          <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl">
                {t("campaign.count", { count: campaigns.length })}
              </h2>
              <Button size="sm" onClick={() => setCreateMode(!createMode)}>
                {createMode ? t("common.cancel") : t("campaign.create")}
              </Button>
            </div>

            {campaignsFailed && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm flex items-center justify-between gap-3">
                <span>
                  {describeError(campaignsError, t("campaign.toastLoadFailed"))}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={refetchCampaigns}
                >
                  {t("common.retry")}
                </Button>
              </div>
            )}

            {/* Create form */}
            {createMode && (
              <div className="border border-c-gray-400/30 rounded-lg p-4 space-y-3">
                <div>
                  <Label>{t("campaign.name")}</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t("campaign.namePlaceholder")}
                  />
                </div>
                <div>
                  <Label>{t("campaign.triggerType")}</Label>
                  <Select
                    value={newTriggerType}
                    onChange={(e) => {
                      const type = e.target.value;
                      setNewTriggerType(type);
                      // Seed the config from the schema so what the form shows
                      // is what gets saved. Previously the defaults were only
                      // a *display* fallback, so an untouched form submitted
                      // `config: {}` and the backend rejected it with
                      // "push_title_template must not be empty" — the operator
                      // saw the text in the box and still got a 400.
                      setNewConfig(
                        type ? configFromSchema(fieldsFor(type)) : {},
                      );
                    }}
                  >
                    <option value="">—</option>
                    {triggerTypes.map((tt) => (
                      <option key={tt.triggerType} value={tt.triggerType}>
                        {tt.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {newTriggerType && (
                  <>
                    <div className="flex justify-end">
                      <CopySqlButton
                        triggerType={newTriggerType}
                        config={newConfig}
                      />
                    </div>
                    <CampaignForm
                      fields={fieldsFor(newTriggerType)}
                      config={newConfig}
                      onChange={setNewConfig}
                      disabled={saving}
                      triggerType={newTriggerType}
                      placeholders={placeholdersFor(newTriggerType)}
                      segmentOptions={customSegmentNames}
                    />
                    <SegmentManager />
                  </>
                )}
                <Button onClick={handleCreate} disabled={saving}>
                  {t("campaign.createBtn")}
                </Button>
              </div>
            )}

            <CampaignTable
              campaigns={campaigns}
              triggerTypes={triggerTypes}
              selectedId={selectedId}
              busyId={busyId}
              onSelect={setSelectedId}
              onToggle={handleToggle}
              onRun={handleRun}
              onStop={handleStop}
              onTest={handleTest}
              onDelete={(id) => setDeleteTarget(id)}
            />
          </div>
        </div>

        {/* ── Edit Panel ── */}
        {selectedId && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl">
                  {editName ||
                    t("campaign.campaignFallback", { id: selectedId })}
                  {" — "}
                  {getLabel(selectedCampaign?.triggerType)}
                </h2>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || !editDirty}
                >
                  {t("common.save")}
                </Button>
              </div>

              <div>
                <Label>{t("campaign.name")}</Label>
                <Input
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    markEdited();
                  }}
                  placeholder={t("campaign.namePlaceholder")}
                />
              </div>

              <div className="flex justify-end">
                <CopySqlButton
                  triggerType={selectedCampaign?.triggerType}
                  config={editConfig}
                />
              </div>

              <CampaignForm
                fields={fieldsFor(selectedCampaign?.triggerType)}
                config={editConfig}
                onChange={(next) => {
                  setEditConfig(next);
                  markEdited();
                }}
                disabled={saving}
                triggerType={selectedCampaign?.triggerType}
                placeholders={placeholdersFor(selectedCampaign?.triggerType)}
                campaignId={selectedId}
                segmentOptions={customSegmentNames}
              />
              <SegmentManager campaignId={selectedId} />
            </div>
          </div>
        )}

        {/* ── Run progress ── */}
        {selectedId && runState && (
          <CampaignRunProgress run={runState} onStop={handleStop} />
        )}

        {/* ── Stats Panel ── */}
        {selectedId && statsData && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4">
              <h2 className="text-xl mb-3">{t("campaign.stats")}</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">
                    {statsData.candidates}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statCandidates")}
                  </div>
                </div>
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {statsData.sent}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statSent")}
                  </div>
                </div>
                <div className="bg-c-gray-500/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {statsData.failed}
                  </div>
                  <div className="text-c-gray-400 text-sm">
                    {t("campaign.statFailed")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Log Panel ── */}
        {selectedId && (
          <div className="w-full shrink-0">
            <div className="bg-c-gray-600 rounded-lg p-4 space-y-2">
              <h2 className="text-xl">{t("campaign.log")}</h2>
              {logFailed ? (
                <div className="text-red-400 text-sm py-4 text-center">
                  {t("campaign.toastLogFailed")}
                </div>
              ) : (
                <CampaignLog
                  log={logData}
                  offset={logOffset}
                  onOffsetChange={setLogOffset}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("campaign.confirmDeleteTitle")}
        itemName={deleteTargetName}
        message={t("campaign.confirmDeleteMessage", { item: deleteTargetName })}
        confirmText={t("common.delete")}
        onConfirm={handleDelete}
      />

      {/* ── Test campaign dialog ── */}
      <ConfirmDialog
        open={testTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setTestTarget(null);
            setTestPhone("");
          }
        }}
        title={t("campaign.testDialogTitle")}
        message={t("campaign.testDialogDesc")}
        customContent={
          <div className="space-y-2">
            <Label>{t("campaign.testDialogPhone")}</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder={t("campaign.testDialogPhonePlaceholder")}
              disabled={testMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !testMutation.isPending) {
                  handleTestSend();
                }
              }}
            />
          </div>
        }
        confirmText={t("campaign.testDialogSend")}
        cancelText={t("campaign.testDialogCancel")}
        // Primary: sending a test is the dialog's affirmative action, and it
        // read as a disabled/secondary action while "secondary" was set.
        confirmVariant="default"
        onConfirm={handleTestSend}
        disabled={testMutation.isPending}
      />
    </PageShell>
  );
}

/**
 * Build a campaign config from a trigger's field schema.
 *
 * Only fields that actually declare a default are included, so the saved
 * config stays explicit without inventing keys the trigger does not use.
 * This is what keeps the form's contents and the persisted config identical
 * — a display-only fallback would let an untouched form submit an empty
 * config and be rejected by the backend's required-template validation.
 *
 * @param {Array<{name: string, default: unknown}>} fields
 * @returns {object}
 */
function configFromSchema(fields) {
  const config = {};
  for (const field of fields) {
    if (field.default !== undefined && field.default !== null) {
      config[field.name] = field.default;
    }
  }
  return config;
}

/**
 * Surface the backend's error message instead of a bare "request failed".
 *
 * `apiFetch` callers throw `await resp.text()`, so the useful part — "Campaign
 * has 12 unfulfilled bonus promises" — was being caught and discarded by a
 * bare `catch {}`.
 */
function describeError(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}
